import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Button,
  Drawer,
  message,
  Modal,
  Space,
  Table,
  Tooltip,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { Client } from "../../../core/api-client";
import { API_GET_ALL_STAFF_LIST } from "../../../shared/routes/private.routes";
import {
  deleteMilestone,
  getMilestonesByProject,
  patchMilestoneOrder,
} from "../../../core/store/reducers/milestone.slice";
import { showNotification } from "../../../shared/utils/utils";
import CreateEditMilestoneComponent from "./create-edit-milestone.component";

interface Props {
  visible: boolean;
  onClose: () => void;
  selectedEntity: any;
  onSuccess?: () => void;
}

const MilestonesDrawerComponent: React.FC<Props> = ({
  visible,
  onClose,
  selectedEntity,
  onSuccess,
}: Props) => {
  const dispatch = useDispatch();
  const [milestones, setMilestones] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [staffOptions, setStaffOptions] = useState<{ value: string; label: string }[]>([]);
  const [createEditVisible, setCreateEditVisible] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<any | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [milestoneToDelete, setMilestoneToDelete] = useState<string | null>(null);
  const [reorderLoading, setReorderLoading] = useState<string | null>(null);

  const projectId = selectedEntity?.projectId;

  const fetchMilestones = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    dispatch(getMilestonesByProject(projectId))
      .then((val: any) => {
        const data = val?.payload?.data?.data;
        setMilestones(Array.isArray(data) ? data : []);
      })
      .finally(() => setLoading(false));
  }, [dispatch, projectId]);

  const fetchStaff = useCallback(() => {
    Client.get(API_GET_ALL_STAFF_LIST).then((res: any) => {
      const list = res?.data?.data || [];
      setStaffOptions(
        list.map((s: any) => ({ value: s.staffId, label: s.staffName || s.employeeId || s.staffId }))
      );
    });
  }, []);

  useEffect(() => {
    if (visible && projectId) {
      fetchMilestones();
      fetchStaff();
    }
  }, [visible, projectId, fetchMilestones, fetchStaff]);

  const handleAdd = () => {
    setEditingMilestone(null);
    setCreateEditVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingMilestone(record);
    setCreateEditVisible(true);
  };

  const handleCreateEditSuccess = () => {
    fetchMilestones();
    setCreateEditVisible(false);
    setEditingMilestone(null);
    onSuccess?.();
  };

  const handleDeleteClick = (milestoneId: string) => {
    setMilestoneToDelete(milestoneId);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    if (!milestoneToDelete) return;
    dispatch(deleteMilestone(milestoneToDelete)).then((val: any) => {
      const data = val?.payload?.data;
      if (data?.statusCode === 200 || data?.message) {
        showNotification("Milestone deleted successfully", "success");
        setDeleteModalVisible(false);
        setMilestoneToDelete(null);
        fetchMilestones();
        onSuccess?.();
      }
    });
  };

  const moveOrder = (record: any, direction: "up" | "down") => {
    const idx = milestones.findIndex((m) => m.milestoneId === record.milestoneId);
    if (idx < 0) return;
    if (direction === "up" && idx === 0) return;
    if (direction === "down" && idx >= milestones.length - 1) return;
    // API order is 1-based: first item = 1, second = 2, etc.
    const targetOrder = direction === "up" ? idx : idx + 2;
    setReorderLoading(record.milestoneId);
    dispatch(
      patchMilestoneOrder({
        milestoneId: record.milestoneId,
        order: targetOrder,
      })
    )
      .then((val: any) => {
        const data = val?.payload?.data;
        if (data?.statusCode === 200 || data?.data) {
          showNotification("Order updated", "success");
          fetchMilestones();
        }
      })
      .finally(() => setReorderLoading(null));
  };

  const columns = [
    {
      title: "Order",
      key: "order",
      width: 80,
      render: (_: any, record: any, index: number) => (
        <Space>
          <Tooltip title="Move up">
            <Button
              type="link"
              icon={<ArrowUpOutlined />}
              disabled={index === 0 || reorderLoading !== null}
              loading={reorderLoading === record.milestoneId}
              onClick={() => moveOrder(record, "up")}
            />
          </Tooltip>
          <Tooltip title="Move down">
            <Button
              type="link"
              icon={<ArrowDownOutlined />}
              disabled={index === milestones.length - 1 || reorderLoading !== null}
              loading={reorderLoading === record.milestoneId}
              onClick={() => moveOrder(record, "down")}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "%",
      dataIndex: "percentage",
      key: "percentage",
      width: 70,
      render: (v: any) => (v != null ? Number(v).toFixed(4) : "-"),
    },
    {
      title: "Est. Billing Date",
      dataIndex: "estimatedBillingDate",
      key: "estimatedBillingDate",
      width: 120,
      render: (v: string) => (v ? moment(v).format("DD-MMM-YYYY") : "-"),
    },
    {
      title: "Invoice Status",
      dataIndex: "invoiceStatus",
      key: "invoiceStatus",
      width: 100,
    },
    {
      title: "Payment",
      dataIndex: "paymentReceived",
      key: "paymentReceived",
      width: 80,
      render: (v: boolean) => (v ? "Yes" : "No"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right" as const,
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          <Button
            type="link"
            icon={<DeleteOutlined style={{ color: "#FF0000" }} />}
            onClick={() => handleDeleteClick(record.milestoneId)}
            title="Delete"
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Drawer
        open={visible}
        onClose={onClose}
        title={`Milestones - ${selectedEntity?.l2ProjectName || selectedEntity?.projectName || "Project"}`}
        width="90%"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Milestone
          </Button>
        }
      >
        <Table
          size="small"
          loading={loading}
          dataSource={milestones}
          columns={columns}
          rowKey="milestoneId"
          pagination={false}
          scroll={{ x: 800 }}
        />
      </Drawer>

      <CreateEditMilestoneComponent
        visible={createEditVisible}
        onClose={() => {
          setCreateEditVisible(false);
          setEditingMilestone(null);
        }}
        projectId={projectId || ""}
        poValue={selectedEntity?.poValue}
        selectedMilestone={editingMilestone}
        staffOptions={staffOptions}
        onSuccess={handleCreateEditSuccess}
      />

      <Modal
        title="Delete Milestone"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => {
          setDeleteModalVisible(false);
          setMilestoneToDelete(null);
        }}
      >
        <p>Are you sure you want to delete this milestone?</p>
      </Modal>
    </>
  );
};

export default MilestonesDrawerComponent;
